import React from 'react';
import { AlertTriangle, FileText, Users, Cog, Shield, CheckCircle } from 'lucide-react';
import './Home.css'; // Assuming shared styles

const RiskDisclosure = () => {
  return (
    <div className="homeContainer" style={{paddingTop: '4rem'}}>
      <section className="faq bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-500" />
              <h1 className="sectionTitle">Risk Disclosure & Platform Disclaimer</h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Important information about investing through our fractional real estate platform.
            </p>
          </div>
          <div className="prose prose-lg max-w-none bg-card/30 backdrop-blur-sm rounded-3xl p-8 border border-border/50 space-y-8">
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">1. Nature of the Platform</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              This platform is a technology-enabled marketplace that facilitates fractional ownership in real estate. It provides investors with access to curated land and property opportunities, transaction management, and resale facilities.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              👉 The platform does not itself own, sell, or develop properties listed.
            </p>

            <div className="flex items-center space-x-3">
              <Users className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">2. Ownership Structure</h2>
            </div>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2">
              <li>Each property is held in a Special Purpose Vehicle (SPV), usually a Private Limited Company incorporated under Indian law.</li>
              <li>Investors are allotted equity shares in the SPV, which directly holds the property.</li>
              <li>All legal rights, obligations, and liabilities related to the property rest with the SPV and its shareholders, not with the platform.</li>
            </ul>

            <div className="flex items-center space-x-3">
              <Cog className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">3. Role of the Platform</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-3">The platform's responsibilities are limited to:</p>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-1">
              <li>Identifying and listing curated property opportunities.</li>
              <li>Facilitating investor onboarding, KYC, and documentation.</li>
              <li>Enabling secure payments through escrow arrangements.</li>
              <li>Providing a digital dashboard for ownership records, project updates, and transaction history.</li>
              <li>Offering a resale marketplace for fractional ownership transfers.</li>
              <li>Communicating updates, reports, and information received from the SPV.</li>
            </ul>

            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">4. Exclusions & Limitations</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-3">The platform is not responsible for:</p>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-1">
              <li>Title disputes, legal claims, or encumbrances that may affect the property.</li>
              <li>Market performance, appreciation, or depreciation of the property.</li>
              <li>Delays in project execution, approvals, or land pooling under government schemes.</li>
              <li>Distribution of profits, dividends, or exit proceeds — which are managed by the SPV.</li>
              <li>Taxation liabilities of investors.</li>
            </ul>

            <div className="flex items-center space-x-3">
              <CheckCircle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">5. Investor Responsibilities</h2>
            </div>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-1">
              <li>Investors must conduct independent due diligence and review all legal and financial documents of the SPV before investing.</li>
              <li>All investments are made directly into the SPV, not into the platform.</li>
              <li>Investors acknowledge that ownership rights are between the SPV and its shareholders, and not against the platform.</li>
              <li>Investors are responsible for complying with applicable tax and legal requirements.</li>
            </ul>

            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">6. No Guaranteed Returns</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              The platform does not guarantee or assure returns, profits, or timelines. All projections are indicative estimates only.
            </p>

            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">7. Risk Factors</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Investments are subject to market, regulatory, liquidity, legal, and execution risks. Investors should invest only after assessing their own risk appetite and seeking professional advice.
            </p>

            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-foreground m-0">8. Governing Law</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              All activities facilitated by the platform are subject to applicable laws of India. Any disputes relating to ownership or property are strictly between the SPV and its shareholders.
            </p>

            <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-400">
              <h2 className="text-lg font-semibold text-orange-700 mb-2">⚖️ Important Note</h2>
              <p className="text-orange-800 leading-relaxed">
                The platform is purely a facilitator and enabler of fractional ownership opportunities. It does not bear legal responsibility for property ownership, performance, or management. Investors participate at their own discretion and risk, with ownership rights arising solely from their shareholding in the SPV.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RiskDisclosure;
